import React, { useEffect, useState } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Icons } from './Icons';
import { formatPrice } from '../utils/helpers';
import { track } from '../utils/analytics';

const STATUS_STYLE = {
  Active:    { bg: 'rgba(109,174,114,0.18)', color: '#4F8F53' },
  'Past Due': { bg: 'rgba(224,123,111,0.18)', color: '#C0392B' },
  Canceled:  { bg: 'rgba(160,147,127,0.2)',  color: '#8B7D6B' },
  Expired:   { bg: 'rgba(160,147,127,0.2)',  color: '#8B7D6B' },
  Pending:   { bg: 'rgba(196,151,90,0.18)',  color: '#A07840' },
};

const TXN_STATUS_STYLE = {
  settled:            { color: '#4F8F53' },
  submitted_for_settlement: { color: '#4F8F53' },
  settling:           { color: '#4F8F53' },
  authorized:         { color: '#A07840' },
  processor_declined: { color: '#C0392B' },
  failed:             { color: '#C0392B' },
  gateway_rejected:   { color: '#C0392B' },
  voided:             { color: '#8B7D6B' },
};

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatTxnStatus(status) {
  if (!status) return '—';
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function PaymentMethodLine({ pm }) {
  if (!pm) return null;
  if (pm.type === 'card') {
    const exp = pm.expirationMonth && pm.expirationYear
      ? ` · exp ${pm.expirationMonth}/${String(pm.expirationYear).slice(-2)}`
      : '';
    return <span>{pm.cardType || 'Card'} •••• {pm.last4}{exp}</span>;
  }
  if (pm.type === 'paypal') {
    return <span>PayPal ({pm.email})</span>;
  }
  return null;
}

// How long they stayed, which is the number churn is read against.
//
// Nothing local records when the subscription began — the Firestore document
// carries `currentPeriodStart`, which is the *current* month and would report
// every cancellation as a few days old. The first transaction Braintree returns
// is the real start date. An account with no transactions yet (a subscription
// created but never charged) returns null, and `track` drops the parameter
// rather than sending a zero that would read as same-day churn.
export function daysSubscribed(details) {
  const transactions = details?.transactions || [];
  const first = transactions[transactions.length - 1];
  if (!first?.createdAt) return null;
  const started = Date.parse(first.createdAt);
  if (Number.isNaN(started)) return null;
  return Math.max(0, Math.floor((Date.now() - started) / 86400000));
}

export default function SubscriptionDetailsModal({ onClose }) {
  const { getSubscriptionDetails, cancelSubscription } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [details, setDetails] = useState(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    let cancelledEffect = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await getSubscriptionDetails();
        if (!cancelledEffect) setDetails(data);
      } catch (e) {
        if (!cancelledEffect) setError(e?.message || 'Could not load your subscription details.');
      } finally {
        if (!cancelledEffect) setLoading(false);
      }
    })();
    return () => { cancelledEffect = true; };
    // eslint-disable-line
  }, []);

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      await cancelSubscription();
      setCancelled(true);
      setConfirmingCancel(false);
      track('subscription_cancelled', {
        days_subscribed: daysSubscribed(details),
        status_at_cancel: details?.status || 'unknown',
      });
    } catch (e) {
      setCancelError(e?.message || 'Could not cancel your subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const statusStyle = STATUS_STYLE[details?.status] || STATUS_STYLE.Pending;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(44,36,24,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
        background: '#FDFBF7', borderRadius: 20,
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          background: '#2C2418', padding: '24px 28px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#C4975A', display: 'flex' }}><Icons.Star size={22} /></span>
            <h2 style={{ margin: 0, color: '#F5EDE3', fontSize: 20, fontFamily: "'Crimson Pro', Georgia, serif", fontWeight: 700 }}>
              Kinship Trees Plus
            </h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#A0937F', cursor: 'pointer', display: 'flex' }}>
            <Icons.Close size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px 28px' }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
              <div style={{
                width: 28, height: 28, border: '3px solid #E8E0D4',
                borderTop: '3px solid #C4975A', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
            </div>
          )}

          {!loading && error && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#C0392B' }}>{error}</p>
              <button
                onClick={() => { setLoading(true); setError(''); getSubscriptionDetails().then(({ data }) => setDetails(data)).catch(e => setError(e?.message || 'Could not load your subscription details.')).finally(() => setLoading(false)); }}
                style={{
                  padding: '10px 24px', borderRadius: 10, border: '1px solid rgba(107,94,79,0.3)',
                  background: 'rgba(107,94,79,0.1)', color: '#6B5E4F', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && cancelled && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ margin: '0 0 8px', fontSize: 14, color: '#2C2418', fontWeight: 600 }}>Your subscription is canceled</p>
              <p style={{ margin: '0 0 24px', fontSize: 13, color: '#6B5E4F', lineHeight: 1.6 }}>
                You'll keep full access until the end of your current billing period{details?.paidThroughDate ? ` (${formatDateTime(details.paidThroughDate)})` : ''}.
              </p>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 28px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #C4975A 0%, #A07840 100%)',
                  color: '#2C2418', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Done
              </button>
            </div>
          )}

          {!loading && !error && !cancelled && details && (
            <>
              {/* Status + price */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
                  background: statusStyle.bg, color: statusStyle.color,
                  textTransform: 'uppercase', letterSpacing: 0.6,
                }}>
                  {details.status || 'Unknown'}
                </span>
                {details.planPrice && (
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#2C2418' }}>
                    {/* effectivePrice is what Braintree actually bills — the
                        plan price less any discounts. Showing planPrice alone
                        would contradict the transaction list further down. */}
                    {details.discounts?.length > 0 && (
                      <span style={{ fontWeight: 500, color: '#8B7D6B', textDecoration: 'line-through', marginRight: 6 }}>
                        {formatPrice(details.planPrice, details.planCurrency)}
                      </span>
                    )}
                    {formatPrice(details.effectivePrice || details.planPrice, details.planCurrency)}
                    <span style={{ fontWeight: 500, color: '#8B7D6B' }}>/month</span>
                  </span>
                )}
              </div>

              {details.discounts?.length > 0 && (
                <div style={{ marginBottom: 16, fontSize: 12, color: '#6B5E4F' }}>
                  {details.discounts.map((d) => (
                    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{d.name || d.id}</span>
                      <span>
                        −{formatPrice(d.amount, details.planCurrency)}
                        {d.numberOfBillingCycles && !d.neverExpires
                          ? ` · ${d.currentBillingCycle || 0} of ${d.numberOfBillingCycles} months used`
                          : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Billing / payment method */}
              <div style={{ background: '#F5EDE3', borderRadius: 12, padding: '14px 16px', border: '1.5px solid #E8D8C0', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#6B5E4F', marginBottom: details.paymentMethod ? 8 : 0 }}>
                  <span>{details.status === 'Canceled' ? 'Access ends' : 'Next billing date'}</span>
                  <strong style={{ color: '#2C2418' }}>
                    {formatDateTime(details.status === 'Canceled' ? details.paidThroughDate : details.nextBillingDate)}
                  </strong>
                </div>
                {details.paymentMethod && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#6B5E4F' }}>
                    <span>Payment method</span>
                    <strong style={{ color: '#2C2418' }}><PaymentMethodLine pm={details.paymentMethod} /></strong>
                  </div>
                )}
              </div>

              {/* Transaction history */}
              <p style={{ margin: '0 0 8px 2px', fontSize: 11, color: '#A0937F', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700 }}>
                Payment History
              </p>
              <div style={{ border: '1px solid rgba(196,151,90,0.2)', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
                {details.transactions.length === 0 ? (
                  <p style={{ margin: 0, padding: '16px', textAlign: 'center', fontSize: 12.5, color: '#8B7D6B' }}>
                    No charges yet
                  </p>
                ) : (
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {details.transactions.map((t, i) => (
                      <div key={t.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', fontSize: 12.5,
                        borderTop: i === 0 ? 'none' : '1px solid rgba(196,151,90,0.12)',
                      }}>
                        <span style={{ color: '#2C2418' }}>{formatDateTime(t.createdAt)}</span>
                        <span style={{ color: (TXN_STATUS_STYLE[t.status] || {}).color || '#8B7D6B', fontWeight: 600 }}>
                          {formatTxnStatus(t.status)}
                        </span>
                        <span style={{ color: '#2C2418', fontWeight: 700 }}>
                          {formatPrice(t.amount, details.planCurrency)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cancel */}
              {details.status !== 'Canceled' && details.status !== 'Expired' && (
                confirmingCancel ? (
                  <div style={{ background: 'rgba(224,123,111,0.1)', borderRadius: 12, padding: '14px 16px', border: '1.5px solid rgba(224,123,111,0.3)' }}>
                    <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#6B5E4F', lineHeight: 1.55 }}>
                      Cancel your Kinship Trees Plus subscription? You'll keep full access until the end of your current billing period.
                    </p>
                    {cancelError && <p style={{ margin: '0 0 12px', fontSize: 12, color: '#C0392B' }}>{cancelError}</p>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => setConfirmingCancel(false)}
                        disabled={cancelling}
                        style={{
                          flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid rgba(107,94,79,0.3)',
                          background: 'transparent', color: '#6B5E4F', fontSize: 12.5, fontWeight: 600,
                          cursor: cancelling ? 'default' : 'pointer', fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Keep Subscription
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={cancelling}
                        style={{
                          flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
                          background: '#C0392B', color: '#FDFBF7', fontSize: 12.5, fontWeight: 700,
                          cursor: cancelling ? 'default' : 'pointer', opacity: cancelling ? 0.7 : 1,
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        {cancelling ? 'Canceling…' : 'Cancel Subscription'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingCancel(true)}
                    style={{
                      width: '100%', padding: '11px 0', borderRadius: 8,
                      background: 'rgba(107,94,79,0.1)', border: '1px solid rgba(107,94,79,0.3)',
                      color: '#8B7D6B', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    Cancel Subscription
                  </button>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
